import { MarkdownRenderer } from '@components/markdown-renderer'
import { Text } from '@components/text'
import type colorStyles from '@components/text/text-colors.module.scss'
import textColorStyles from '@components/text/text-colors.module.scss'
import type typeStyles from '@components/text/type-styles.module.scss'
import type { CustomRenderers } from '@contentful-components/rich-text/render'
import { usePersonalizations } from '@lib/hooks/usePersonalizations'
import type { OptionallyResponsiveProp } from '@lib/utils/responsiveProps'
import { renderTemplate } from '@lib/utils/text'
import { normalizeStepUrl } from '@shared/util/link'
import Link from 'next/link'
import { useMemo } from 'react'
import type { FunctionComponent, ReactNode } from 'react'

import {
    replaceForDisclaimer,
    replaceForReadyFont,
    replaceForSuperScript,
    replaceForSymbol,
} from './utils'

const EXTERNAL_URL_REGEX = /https?:\/\//

export const Markdown: FunctionComponent<Props> = ({
    markdown,
    accentColor = 'primary',
    boldColor,
    tag = 'p',
    typeStyle = 'body-medium',
    color = 'default',
}) => {
    const personalizations = usePersonalizations()

    // Validate and ensure color is a valid key from colorStyles
    const validatedColor = color && color in textColorStyles ? color : 'default'

    // get custom renderers - memoized to ensure color updates
    const renderers = useMemo(
        () =>
            getRenderers({
                accentColor,
                tag,
                typeStyle,
                boldColor,
                color: validatedColor,
            }),
        [accentColor, tag, typeStyle, boldColor, validatedColor],
    )

    if (!markdown) {
        return null
    }

    const personalizedSource = renderTemplate(markdown, personalizations)

    return (
        <MarkdownRenderer
            key={validatedColor}
            renderers={renderers}
            source={personalizedSource}
        />
    )
}

const getRenderers = ({
    accentColor,
    boldColor,
    tag,
    typeStyle,
    color,
}: Omit<Props, 'markdown'> & {
    color: keyof typeof colorStyles
}): CustomRenderers => {
    return {
        text: (text) => {
            // if text from contentful has a literal new line in it, replace it with a real one
            if (text.includes('\\n')) {
                text = text.replace(/\\n/g, ' \n')
            }

            return text.split(/\n/g).reduce((children, textSegment, index) => {
                // handle superscript replacements (E.g. replace ^1 in text with <sup>1</sup>)
                if (textSegment.includes('^')) {
                    // find all instances of ^1 or ^2 etc and replace with a string sup tag
                    const replacedText = replaceForSuperScript(textSegment)

                    return [
                        ...children,
                        index > 0 && <br key={index} />,
                        <span
                            key={replacedText}
                            dangerouslySetInnerHTML={{
                                __html: replacedText,
                            }}
                            className={textColorStyles[color]}
                        />,
                    ]
                }

                if (
                    textSegment.includes('~sym') ||
                    textSegment.includes('~supsym')
                ) {
                    const replacedText = replaceForSymbol(textSegment)

                    return [
                        ...children,
                        index > 0 && <br key={index} />,
                        <span
                            key={replacedText}
                            dangerouslySetInnerHTML={{
                                __html: replacedText,
                            }}
                            className={textColorStyles[color]}
                        />,
                    ]
                }

                if (textSegment.includes('~disclaimer:')) {
                    const replacedText = replaceForDisclaimer(textSegment)

                    return [
                        ...children,
                        index > 0 && <br key={index} />,
                        <span
                            key='replacedText'
                            dangerouslySetInnerHTML={{
                                __html: replacedText,
                            }}
                            className={textColorStyles[color]}
                            style={{ fontSize: '12px' }}
                        />,
                    ]
                }

                if (textSegment.match(/~ready:/i)) {
                    const replacedText = replaceForReadyFont(textSegment)

                    return [
                        ...children,
                        index > 0 && <br key={index} />,
                        <span
                            key={replacedText}
                            dangerouslySetInnerHTML={{
                                __html: replacedText,
                            }}
                            className={textColorStyles[color]}
                        />,
                    ]
                }

                return [
                    ...children,
                    index > 0 && <br key={index} />,
                    textSegment,
                ]
            }, [] as ReactNode[])
        },
        italic: (node) => (
            <Text typeStyle={typeStyle} color={accentColor} tag='span'>
                {node}
            </Text>
        ),
        bold: (node) => (
            <Text
                typeStyle={typeStyle}
                color={boldColor ?? color}
                tag='span'
                bold
            >
                {node}
            </Text>
        ),
        listItem: (_, children) => (
            <Text tag='li' typeStyle={typeStyle} color={color}>
                {children}
            </Text>
        ),
        paragraph: (_, children) => (
            <Text tag={tag} typeStyle={typeStyle} color={color}>
                {children}
            </Text>
        ),
        link: (node, children) => {
            const uri = normalizeStepUrl(node.data.uri)
            const isExternalUrl = uri.match(EXTERNAL_URL_REGEX)

            return (
                <Link href={uri} target={isExternalUrl ? '_blank' : '_self'}>
                    <Text typeStyle={typeStyle} tag='span' bold>
                        {children}
                    </Text>
                </Link>
            )
        },
    }
}

interface Props {
    accentColor?: keyof typeof colorStyles
    boldColor?: keyof typeof colorStyles
    color?: keyof typeof colorStyles
    markdown?: string
    typeStyle?: OptionallyResponsiveProp<keyof typeof typeStyles>
    tag?: 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span' | 'li'
}
